import { Close as CloseIcon } from '@material-ui/icons';
import cx from 'classnames';
import { FC, useCallback, useState } from 'react';
import { Navigation } from 'swiper';
import { Pagination } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

import { WithStyles, withStyles } from '@core/theme/utils/with-styles';
import { Button } from '@shared/components/button';
import { Dialog, DialogTheme } from '@shared/components/dialog';
import { IImageDetails } from '@shared/models/IImages';

import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';

import { styles } from './Carousel.styles';
import './carouser.styles.scss';

export interface CarouselProps extends WithStyles<typeof styles> {
  galleryImages?: IImageDetails[];
  images: IImageDetails[];
  showControls?: boolean;
  showIndicators?: boolean;
  withGallery?: boolean;
}

const CarouselComponent: FC<CarouselProps> = ({
  classes,
  galleryImages,
  images,
  showControls = true,
  showIndicators = true,
  withGallery = false,
}) => {
  const [openGallery, setOpenGallery] = useState(false);
  const [previewIndex, setPreviewIndex] = useState(0);

  const handleImageClick = (idx: number) => {
    setPreviewIndex(idx);
    setOpenGallery(true);
  };

  const renderImages = useCallback(
    (dialog?: boolean) => {
      const imagesToRender = dialog ? galleryImages ?? images : images;

      return imagesToRender.map((image, idx) => (
        <SwiperSlide key={`image-${image.id}-${idx}`}>
          <img
            src={image.url}
            className={cx(classes.image, {
              [classes.fullImage]: dialog,
              [classes.imageClickable]: withGallery && !dialog,
            })}
            alt=""
            onClick={withGallery && !dialog ? () => handleImageClick(idx) : undefined}
          />
        </SwiperSlide>
      ));
    },
    [galleryImages, images, withGallery, classes]
  );

  const carousel = useCallback(
    (dialog?: boolean) => {
      const imagesToRender = dialog ? galleryImages ?? images : images;

      return (
        <Swiper
          initialSlide={dialog ? previewIndex : 0}
          pagination={
            showIndicators && {
              clickable: true,
              clickableClass: cx(classes.pagination, { [classes.paginationGallery]: withGallery }),
            }
          }
          navigation={showControls}
          modules={[Navigation, Pagination]}
          onSlideChange={dialog ? undefined : (swiper) => setPreviewIndex(swiper.activeIndex)}
        >
          {!!imagesToRender.length && renderImages(dialog)}
        </Swiper>
      );
    },
    [withGallery, galleryImages, images, renderImages, previewIndex, showControls, showIndicators, classes]
  );

  const handleCloseGallery = () => {
    setOpenGallery(false);
  };

  return (
    <div className={classes.root}>
      <div className={classes.previewCarousel}>{carousel()}</div>
      {withGallery && (
        <div className={classes.gallery} onClick={() => setOpenGallery(true)}>
          <i className={`icon icon-image ${classes.galleryImage}`} />
          <span className={classes.galleryLabel}>Gallery</span>
        </div>
      )}
      <Dialog
        classes={{ root: classes.dialog }}
        open={openGallery}
        theme={DialogTheme.legacy}
        withFooter={false}
        withTransition
        onClose={handleCloseGallery}
      >
        {carousel(true)}
        <Button
          variant="outlined"
          size="small"
          color="default"
          text={<CloseIcon className={classes.headerIcon} />}
          onClick={handleCloseGallery}
          classes={{
            root: classes.headerBtn,
            outlinedSizeSmall: classes.headerBtnSmall,
          }}
        />
      </Dialog>
    </div>
  );
};

export const Carousel = withStyles(styles)(CarouselComponent);
